import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { ToastService } from '../../shared/services/toast.service';
import { environment } from '../../../environments/environment';

/**
 * Auth endpoints whose errors are surfaced by their own caller (a form's inline
 * message, or a silent init()) rather than by a global toast/redirect. Shared by
 * authInterceptor (no session-expired logout) and errorToastInterceptor (no generic
 * "Http failure response" toast on top of the precise in-form message).
 */
export const PUBLIC_AUTH_PATHS = ['/auth/login', '/auth/admin-login', '/auth/register',
  '/auth/verify-payment', '/auth/verify-setup-token', '/auth/set-password', '/auth/me',
  '/auth/logout'];

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const toast = inject(ToastService);

  // Always send the HttpOnly ss_token cookie (withCredentials).
  // In production (same-origin, HTTPS) this is the only auth mechanism —
  // XSS-safe, no change from original behaviour.
  let outgoing = request.clone({ withCredentials: true });

  // In staging/dev (cross-origin, HTTP localhost): also attach a Bearer token
  // when available, because the Secure + SameSite=Strict cookie cannot be set
  // or sent cross-origin from localhost. The auth middleware checks Bearer
  // first, then falls back to the cookie — both are valid.
  if (!environment.production) {
    const token = authService.getToken();
    if (token !== null) {
      outgoing = outgoing.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    }
  }

  return next(outgoing).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        if (!PUBLIC_AUTH_PATHS.some((p) => request.url.includes(p))) {
          toast.error('Your session has expired. Please log in again.');
          void authService.logout();
        }
      }
      return throwError(() => error);
    }),
  );
};
